import { Controller } from "@hotwired/stimulus";

// Warns when the chosen text color has poor contrast against the background color.
// Listens to both color inputs and toggles a warning icon based on the WCAG
// contrast ratio. Threshold 4.5:1 is the AA minimum for normal body text.
export default class extends Controller {
  static targets = ["background", "textColor", "warning"];
  static values = { threshold: { type: Number, default: 4.5 } };

  connect() {
    this.check();
  }

  check() {
    if (!this.hasBackgroundTarget || !this.hasTextColorTarget || !this.hasWarningTarget) return;

    const ratio = this.contrastRatio(this.textColorTarget.value, this.backgroundTarget.value);
    const poor = ratio !== null && ratio < this.thresholdValue;

    this.warningTarget.classList.toggle("d-none", !poor);
    if (poor) {
      const text = `Low contrast (${ratio.toFixed(1)}:1) against the background — text may be hard to read. Aim for at least ${this.thresholdValue}:1.`;
      this.warningTarget.setAttribute("data-bs-title", text);
      const tip = window.bootstrap?.Tooltip?.getInstance(this.warningTarget);
      if (tip) tip.setContent({ ".tooltip-inner": text });
    }
  }

  contrastRatio(hexA, hexB) {
    const a = this.relativeLuminance(hexA);
    const b = this.relativeLuminance(hexB);
    if (a === null || b === null) return null;

    const lighter = Math.max(a, b);
    const darker = Math.min(a, b);
    return (lighter + 0.05) / (darker + 0.05);
  }

  relativeLuminance(hex) {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return null;

    const [r, g, b] = rgb.map((c) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  hexToRgb(hex) {
    if (!hex) return null;
    const m = hex.replace("#", "").match(/^([0-9a-f]{6})$|^([0-9a-f]{3})$/i);
    if (!m) return null;

    let h = m[1] || m[2];
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  }
}
